import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useState } from 'react'
import { ArrowLeft, Plus, Pencil, ShieldCheck } from 'lucide-react'
import { toast } from 'sonner'
import { useForms } from '@/hooks/use-forms'
import { useCompliance } from '@/hooks/use-compliance'
import { useAuth } from '@/hooks/use-auth'
import { DynamicFormFields } from '@/components/dynamic-form'
import {
  Page,
  PageHeader,
  Card,
  CardHeader,
  Rows,
  Row,
  RowChevron,
  StatusPill,
  SkeletonRows,
  EmptyState,
  btnPrimary,
  btnSecondary,
} from '@/components/rm'
import { Modal, Field, Input } from '@/components/rm/form'
import { phFormat } from '@/lib/ph'

export const Route = createFileRoute('/_app/forms_/$id')({
  component: FormRecordsPage,
})

function FormRecordsPage() {
  const { id } = Route.useParams()
  const navigate = useNavigate()
  const { form, fields, isLoadingDetail, records, recordsLoading, createRecord } = useForms(id)
  const { issue } = useCompliance()
  const { companyId } = useAuth()
  const [open, setOpen] = useState(false)
  const [name, setName] = useState('')
  const [values, setValues] = useState<Record<string, unknown>>({})
  const [issuing, setIssuing] = useState<number | null>(null)

  if (isLoadingDetail && !form) {
    return (
      <Page>
        <div className="h-6 w-40 animate-pulse rounded bg-canvas-subtle" />
      </Page>
    )
  }
  if (!form) {
    return (
      <Page>
        <PageHeader title="Form not found" />
        <button className={btnSecondary + ' mt-4'} onClick={() => navigate({ to: '/forms' })}>
          <ArrowLeft className="h-4 w-4" /> Back to forms
        </button>
      </Page>
    )
  }

  const reset = () => {
    setName('')
    setValues({})
  }

  // Spec 020: turn a completed entry into a numbered, frozen certificate. Only offered for forms
  // the operator marked as `compliance` — safety records (SWMS, Take 5) are evidence in their own
  // right and do not get issued to a customer with a certificate number.
  const isCompliance = (form?.category ?? 'job') === 'compliance'

  const issueCert = async (record: { id: number; name: string | null; data: Record<string, unknown> | null }) => {
    setIssuing(record.id)
    try {
      const doc = await issue.mutateAsync({
        docType: form!.name,
        // Snapshot the entry as it stands. The DB freezes the row on signing, so what is captured
        // here is what the certificate will always say.
        payload: (record.data ?? {}) as Record<string, unknown>,
        taskId: record.id,
        formRecordId: null,
      })
      toast.success(`Issued ${doc.doc_number}`)
      navigate({ to: '/compliance' })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Could not issue the certificate')
    } finally {
      setIssuing(null)
    }
  }

  const submit = async () => {
    const missing = fields.filter(
      (f) => f.required && !['header', 'button'].includes(f.type) && (values[f.name] === undefined || values[f.name] === '' || values[f.name] === false),
    )
    if (missing.length) {
      toast.error(`Required: ${missing.map((m) => m.name).join(', ')}`)
      return
    }
    try {
      await createRecord.mutateAsync({ name: name.trim() || form.name, data: values })
      reset()
      setOpen(false)
      toast.success('Entry added')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Failed')
    }
  }

  return (
    <Page>
      <button
        onClick={() => navigate({ to: '/forms' })}
        className="mb-3 inline-flex items-center gap-1 text-[13px] font-medium text-token-secondary transition-colors hover:text-token-secondary"
      >
        <ArrowLeft className="h-3.5 w-3.5" /> All forms
      </button>
      <PageHeader
        title={form.name}
        subtitle={`${records.length} ${records.length === 1 ? 'entry' : 'entries'}`}
        actions={
          <>
            <button className={btnSecondary} onClick={() => navigate({ to: '/forms/$id/edit', params: { id } })}>
              <Pencil className="h-4 w-4" /> Edit form
            </button>
            <button className={btnPrimary} onClick={() => setOpen(true)}>
              <Plus className="h-4 w-4" /> Add entry
            </button>
          </>
        }
      />

      <div className="mt-6">
        <Card>
          <CardHeader title="Entries" count={records.length} />
          {recordsLoading ? (
            <SkeletonRows />
          ) : records.length === 0 ? (
            <EmptyState label="No entries yet." cta="Add entry" onCta={() => setOpen(true)} />
          ) : (
            <Rows>
              {records.map((r) => (
                <li key={r.id}>
                  <Row onClick={() => navigate({ to: '/jobs/$id', params: { id: String(r.id) } })}>
                    <span className="min-w-0">
                      <span className="block truncate text-[14px] font-medium text-token-primary">{r.name || 'Entry'}</span>
                      {r.created_at && (
                        <span className="block text-[12px] text-token-secondary">{phFormat(new Date(r.created_at), 'datetime', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' })}</span>
                      )}
                    </span>
                    <span className="flex flex-none items-center gap-2.5">
                      {isCompliance && (
                        <button
                          className={btnSecondary}
                          onClick={(e) => { e.stopPropagation(); issueCert(r) }}
                          disabled={issuing !== null}
                        >
                          <ShieldCheck className="h-4 w-4" />
                          {issuing === r.id ? 'Issuing…' : 'Issue'}
                        </button>
                      )}
                      <StatusPill status={r.status || 'pending'} />
                      <RowChevron />
                    </span>
                  </Row>
                </li>
              ))}
            </Rows>
          )}
        </Card>
      </div>

      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title={`Add entry — ${form.name}`}
        wide
        footer={
          <>
            <button className={btnSecondary} onClick={() => setOpen(false)}>
              Cancel
            </button>
            <button className={btnPrimary} onClick={submit} disabled={createRecord.isPending}>
              {createRecord.isPending ? 'Saving…' : 'Save entry'}
            </button>
          </>
        }
      >
        <div className="space-y-4">
          <Field label="Entry name">
            <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Acme — Tuesday" />
          </Field>
          {fields.length > 0 && <div className="border-t border-token-subtle pt-4" />}
          <DynamicFormFields fields={fields} values={values} onChange={(k, v) => setValues((s) => ({ ...s, [k]: v }))} companyId={companyId ?? ''} />
        </div>
      </Modal>
    </Page>
  )
}
